import React from 'react';
import { Link } from 'react-router-dom';
import { formatNumber, formatDateDashSeparated, isSomething } from '../../../../utils/utils';
import TableActionMenu from '../../../../components/table-action-menu/table-action-menu';

const ProjectColumns = (navigate, companyData, projectGroups) => {
  return [
    {
      dataKey: 'firestoreId',
      name: 'ID',
      width: '10%',
      minWidth: '71px',
      align: 'left',
      sortable: true,
    },
    {
      dataKey: 'projectName',
      name: 'Project Name',
      width: '10%',
      minWidth: '450px',
      align: 'left',
      sortable: true,
      render: (value, rowData) => {
        return (
          <Link
            style={{ color: 'inherit' }}
            to={`/projectsview/${rowData.firestoreId}/details?isGroupView=false`}
            target="_blank"
          >
            {value}
          </Link>
        );
      },
    },
    {
      dataKey: 'addToExistingProject',
      name: 'Project Group',
      width: '10%',
      minWidth: '300px',
      align: 'left',
      sortable: true,
      render: (value) => {
        if (!value || !projectGroups) return ''; // Return empty if no group is linked or projectGroups is undefined

        // Find the project group by ID
        const group = projectGroups[String(value)]; // Ensure matching by string ID
        return group ? (
          <Link style={{ color: 'inherit' }} to={`/projectsview/${value}/details?isGroupView=true`}>
            {group.projectName}
          </Link>
        ) : (
          ''
        );
      },
    },
    {
      dataKey: 'client',
      name: 'Client',
      width: '20%',
      minWidth: '300px',
      align: 'left',
      sortable: true,
      render: (value) => {
        const companyName = companyData[value];
        return companyName ? (
          <Link style={{ color: 'inherit' }} to={`/companiesview/${value}/details`}>
            {companyName}
          </Link>
        ) : (
          value
        );
      },
    },
    {
      dataKey: 'projectLead',
      name: 'Lead',
      width: '10%',
      minWidth: '150px',
      align: 'left',
      sortable: true,
    },
    {
      dataKey: 'dateCreated',
      name: 'Created (mm/dd/yyyy)',
      width: '15%',
      minWidth: '200px',
      align: 'left',
      sortable: true,
    },
    {
      dataKey: 'state',
      name: 'State',
      width: '10%',
      minWidth: '150px',
      align: 'left',
      sortable: true,
    },
    {
      dataKey: 'source',
      name: 'Source',
      width: '10%',
      minWidth: '150px',
      align: 'left',
      sortable: true,
      render: (value) => {
        const sourceOptions = [
          { label: 'RFP', value: 'rfp' },
          { label: 'New Lead', value: 'newLead' },
          { label: 'New Work', value: 'newWork' },
        ];
        const sourceLabel = sourceOptions.find((opt) => opt.value === value)?.label || value;
        return sourceLabel;
      },
    },
    {
      dataKey: 'start',
      name: 'Start (mm/dd/yyyy)',
      width: '10%',
      minWidth: '180px',
      align: 'left',
      sortable: true,
      render: (cellData, rowData) => {
        return cellData;
      },
    },
    {
      dataKey: 'end',
      name: 'Closed (mm/dd/yyyy)',
      width: '10%',
      minWidth: '180px',
      align: 'left',
      sortable: true,
      render: (cellData, rowData) => formatDateDashSeparated(cellData),
    },
    {
      dataKey: 'serviceValue',
      name: 'Forecasted Service Revenue',
      width: '10%',
      minWidth: '260px',
      align: 'left',
      sortable: true,
      render: (cellData, rowData) => {
        return `$ ${cellData}`;
      },
    },
    {
      dataKey: 'financials_hours',
      name: 'Hours (act/est)',
      width: '10%',
      minWidth: '150px',
      align: 'left',
      render: (value, rowData) => {
        const expenseHours = rowData.financials?.expense?.hours || 0;
        const budgetedHours = rowData.financials?.budget?.hours || 0;

        const color = expenseHours <= budgetedHours ? 'green' : 'red';

        return (
          <span style={{ color }}>
            {formatNumber(expenseHours)} hrs / {formatNumber(budgetedHours)} hrs
          </span>
        );
      },
    },
    {
      dataKey: 'plannerServiceValue',
      name: 'Estimated Service Value',
      width: '10%',
      minWidth: '180px',
      align: 'left',
      render: (value) => {
        const formattedValue = formatNumber(value || 0);
        return `$ ${formattedValue}`;
      },
    },
    {
      dataKey: 'material',
      name: 'Material Value',
      width: '10%',
      minWidth: '180px',
      align: 'left',
      render: (value, rowData) => {
        const materialsBudget = rowData?.materials?.reduce(
          (sum, mat) => sum + parseFloat(mat.total || 0),
          0,
        );
        return `$ ${formatNumber(materialsBudget)}`;
      },
    },
    {
      dataKey: 'projectTotal',
      name: 'Total Value',
      width: '10%',
      minWidth: '180px',
      align: 'left',
      render: (value) => {
        const formattedValue = formatNumber(value || 0);
        return `$ ${formattedValue}`;
      },
    },
    {
      dataKey: 'financials_actual_service_value',
      name: 'Actual Service Value',
      width: '10%',
      minWidth: '200px',
      align: 'left',
      render: (value, rowData) => {
        const expenseCost = rowData.financials?.expense?.cost || 0;
        const budgetedCost = rowData.financials?.budget?.cost || 0;

        const color = expenseCost <= budgetedCost ? 'green' : 'red';

        return <span style={{ color }}>$ {formatNumber(expenseCost)}</span>;
      },
    },
    {
      dataKey: '',
      name: 'Total Cost',
      width: '10%',
      minWidth: '200px',
      align: 'left',
      render: (value, rowData) => {
        const actualCost = rowData.financials?.expense?.actualExpense || 0;
        return `$ ${formatNumber(actualCost)}`;
      },
    },
    {
      dataKey: 'invoiceValue',
      name: 'Invoiced Service Value',
      width: '10%',
      minWidth: '180px',
      align: 'left',
      render: (value) => {
        if (!isSomething(value) || value == 0) return '$ 0.00';
        if (Number(value)) return `$ ${formatNumber(value)}`;

        let numValue;

        if (value.startsWith('$')) {
          numValue = value.slice(1);
        } else {
          numValue = value;
        }

        return `$ ${numValue}`;
      },
    },

    {
      dataKey: 'actions',
      name: '',
      width: '5%',
      minWidth: '10px',
      align: 'center',
      render: (value, rowData) => {
        const title = rowData.isGrouped ? 'View Project Group Page' : 'View Project Page';

        const menuOptions = [
          {
            title: title,
            value: 'groupView',
            onClick: () => {
              navigate(
                rowData?.isGrouped
                  ? '/projectsview/11/details?isGroupView=true'
                  : '/projectsview/11/details?isGroupView=true',
              );
            },
          },
          {
            title: 'Add/Edit Project Plan',
            value: 'add/edit',
            onClick: () => {
              navigate(`/projectsview/${rowData.firestoreId}/planner`);
            },
          },
        ];
        return <TableActionMenu noBorder width={175} options={menuOptions} />;
      },
    },
  ];
};

export default ProjectColumns;
